// src/oura/ask/store.ts
//
// Ask Oura (PREVIEW): the conversation per scope, kept in memory for the tab.
// Each Oura route mounts its own shell, so the chat lives here rather than in
// component state: moving from Delivery to the Page workshop keeps the thread.
// Nothing is saved anywhere else and nothing leaves the browser.

import { useSyncExternalStore } from "react";
import type { AskAnswer, AskMessage, AskImage } from "./types";

interface Thread {
    messages: AskMessage[];
}

const threads = new Map<string, Thread>();
const listeners = new Set<() => void>();
const EMPTY: Thread = { messages: [] };

/** Suggestions staff said "Not now" to, for this tab. */
const dismissed = new Set<string>();

/**
 * Answers that have finished revealing, so a remount (a route change, the
 * panel reopening) shows them whole. A number is where staff pressed Stop:
 * the count of words shown.
 */
const settled = new Map<string, number | null>();
/** The answer revealing now, and whether staff asked it to stop. */
let streaming: string | null = null;
const stopAsked = new Set<string>();

let seq = 0;
const nextId = (): string => `m${++seq}`;

/** Answers a server call will fill (live.ts): fillAnswers leaves them open. */
const awaiting = new Set<string>();

function emit() {
    listeners.forEach((l) => l());
}

function subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => {
        listeners.delete(l);
    };
}

export function useThread(key: string): Thread {
    return useSyncExternalStore(subscribe, () => threads.get(key) ?? EMPTY);
}

function updateThread(key: string, change: (t: Thread) => Thread): void {
    threads.set(key, change(threads.get(key) ?? EMPTY));
    emit();
}

/**
 * Post a question and an answer slot; the answer is filled once the records are read.
 * One answer reveals at a time: while one does, nothing is posted (false), and the new
 * answer holds the stream from the moment it is posted, so two quick clicks post one question.
 */
export function postQuestion(key: string, text: string, answer: AskAnswer | null, images?: AskImage[]): boolean {
    if (streaming) return false;
    const reply = nextId();
    const asked = nextId();
    streaming = reply;
    const pics = images && images.length ? images : undefined;
    updateThread(key, (t) => ({
        messages: [...t.messages, { id: asked, from: "staff", text, images: pics }, { id: reply, from: "oura", text, answer, images: pics }],
    }));
    return true;
}

/** An answer a server call brings: what the chat says meanwhile, and the answer. */
export interface OpenAnswer {
    waiting: string;
    answer: Promise<AskAnswer>;
}

/**
 * Fill every answer still waiting on the records, where `fill` gives one: an answer, or a
 * server call (Oura's AI step, the change log, an action preview) whose slot stays open, with
 * `waiting` shown, until the call returns. A failed call fills with `fallback`, so no question
 * is left hanging.
 */
export function fillAnswersWith(key: string, fill: (question: string, images?: AskImage[]) => AskAnswer | OpenAnswer | null, fallback: AskAnswer): void {
    const t = threads.get(key);
    if (!t || !t.messages.some((m) => m.from === "oura" && m.answer === null && !awaiting.has(m.id))) return;
    let changed = false;
    const messages = t.messages.map((m) => {
        if (m.from !== "oura" || m.answer !== null || awaiting.has(m.id)) return m;
        const got = fill(m.text, m.images);
        if (!got) return m;
        changed = true;
        if ("blocks" in got) return { ...m, answer: got };
        awaiting.add(m.id);
        got.answer.then(
            (answer) => resolveAnswer(key, m.id, answer),
            () => resolveAnswer(key, m.id, fallback),
        );
        return { ...m, waiting: got.waiting };
    });
    if (changed) updateThread(key, () => ({ messages }));
}

/** The server's answer for an open question. A thread cleared meanwhile (New chat) drops it. */
export function resolveAnswer(key: string, id: string, answer: AskAnswer): void {
    awaiting.delete(id);
    const t = threads.get(key);
    if (!t || !t.messages.some((m) => m.id === id)) return;
    updateThread(key, (th) => ({ messages: th.messages.map((m) => (m.id === id ? { ...m, answer, waiting: undefined } : m)) }));
}

/** Replace a shown answer in place (an action card after Do it or Not now). */
export function patchAnswer(key: string, id: string, answer: AskAnswer): void {
    const t = threads.get(key);
    if (!t || !t.messages.some((m) => m.id === id)) return;
    updateThread(key, (th) => ({ messages: th.messages.map((m) => (m.id === id ? { ...m, answer } : m)) }));
}

/**
 * Oura speaks first: an answer with no question before it, posted by a
 * screen when something happened there (the Page workshop, after a person's
 * yes). `about` is what it answers, for Ask again.
 */
export function postNotice(key: string, about: string, answer: AskAnswer): void {
    updateThread(key, (t) => ({ messages: [...t.messages, { id: nextId(), from: "oura", text: about, answer }] }));
}

/** Fill every answer still waiting on the records. */
export function fillAnswers(key: string, build: (question: string) => AskAnswer | null): void {
    const t = threads.get(key);
    if (!t || !t.messages.some((m) => m.from === "oura" && m.answer === null)) return;
    let changed = false;
    const messages = t.messages.map((m) => {
        if (m.from !== "oura" || m.answer !== null || awaiting.has(m.id)) return m;
        const answer = build(m.text);
        if (!answer) return m;
        changed = true;
        return { ...m, answer };
    });
    if (changed) updateThread(key, () => ({ messages }));
}

export function newChat(key: string): void {
    if (streaming) stopAsked.add(streaming);
    streaming = null;
    for (const m of threads.get(key)?.messages ?? []) awaiting.delete(m.id);
    updateThread(key, () => ({ messages: [] }));
}

/* ---------------------------- streaming ---------------------------------- */

export function useStreaming(): string | null {
    return useSyncExternalStore(subscribe, () => streaming);
}

/** The answer revealing now, read at the moment of a click (a hook's value can be a render old). */
export const currentStream = (): string | null => streaming;

/** Take the one stream for answer `id` when no other answer holds it. True when `id` holds it. */
export function claimStream(id: string): boolean {
    if (streaming === id) return true;
    if (streaming) return false;
    streaming = id;
    emit();
    return true;
}

/**
 * Give the stream back without settling the answer: it unmounted (the chat closed, the
 * client changed) before it finished, and plays again from the start when it is next shown.
 */
export function releaseStream(id: string): void {
    if (streaming !== id) return;
    streaming = null;
    emit();
}

/** Release the stream if an answer in this thread holds it: its panel is going away. */
export function releaseThreadStream(key: string): void {
    if (streaming && (threads.get(key)?.messages ?? []).some((m) => m.id === streaming)) releaseStream(streaming);
}

export function useStopAsked(id: string): boolean {
    return useSyncExternalStore(subscribe, () => stopAsked.has(id));
}

export function askStop(id: string): void {
    stopAsked.add(id);
    emit();
}

export const settledAt = (id: string): number | null | undefined => (settled.has(id) ? settled.get(id) : undefined);

export function settle(id: string, cut: number | null): void {
    settled.set(id, cut);
    if (streaming === id) streaming = null;
    emit();
}

/* ---------------------------- suggestions -------------------------------- */

export function useDismissed(id: string): boolean {
    return useSyncExternalStore(subscribe, () => dismissed.has(id));
}

export function dismiss(id: string): void {
    dismissed.add(id);
    emit();
}

/* ---------------------------- open / pending ----------------------------- */

/** Whether the panel is open. Kept here so following a source link keeps the chat open on the next screen. */
let open = false;
/** A question typed in the page's Ask bar, asked once the panel for that scope is up. */
let pending: { key: string; text: string } | null = null;

export function useAskOpen(): boolean {
    return useSyncExternalStore(subscribe, () => open);
}

/**
 * What had focus when the chat opened (the Ask bar, the sidebar item, or
 * wherever staff were when they pressed ⌘J), so closing it hands focus back.
 */
let opener: HTMLElement | null = null;
function noteOpener(): void {
    if (typeof document === "undefined") return;
    const el = document.activeElement;
    opener = el instanceof HTMLElement && el !== document.body && !el.closest("[data-ask-oura-panel]") ? el : null;
}

export function setAskOpen(next: boolean): void {
    if (open === next) return;
    if (next) noteOpener();
    open = next;
    emit();
}

/** Close the chat as staff asked (Esc, the X, ⌘J) and put focus back where it was before it opened. */
export function closeAsk(): void {
    const back = opener;
    opener = null;
    setAskOpen(false);
    if (back) window.setTimeout(() => {
        if (back.isConnected) back.focus({ preventScroll: true });
    }, 0);
}

export function toggleAsk(): void {
    if (open) closeAsk();
    else setAskOpen(true);
}

/** Open the chat for a scope and, with text, ask it straight away. */
export function askOura(key: string, text?: string): void {
    const t = text?.trim();
    if (t) pending = { key, text: t };
    if (!open) noteOpener();
    open = true;
    emit();
}

/**
 * Ask the pending question in this scope's thread, when it is this scope's, no answer is
 * revealing and its answer can be built (the records are read). Checking the stream, building
 * the answer, taking the question and posting it are one step, so the question is cleared only
 * once it is in this thread with its answer. Otherwise it stays pending (true: posted): records
 * still being read, an answer that took the stream since the caller last rendered, or another
 * client's chat never drop it or receive it, and the caller asks again when that changes.
 */
export function askPending(key: string, answerFor: (text: string) => AskAnswer | null, opts: { unanswered?: boolean } = {}): boolean {
    if (streaming || !pending || pending.key !== key) return false;
    const queued = pending;
    const answer = answerFor(queued.text);
    // `unanswered`: post it now with its answer open, filled once the records land
    // (fillAnswers), as a question typed in the chat is. The chat then opens on the
    // question, never on the greeting first (29 Oct 2026).
    if ((!answer && !opts.unanswered) || streaming || pending !== queued) return false;
    // Cleared before posting, so the post's emit shows the question gone from pending and in the thread.
    pending = null;
    if (postQuestion(key, queued.text, answer)) return true;
    pending = queued;
    emit();
    return false;
}

export function usePendingFor(key: string): boolean {
    return useSyncExternalStore(subscribe, () => pending?.key === key);
}

/** ⌘ on a Mac, Ctrl elsewhere: the label for the shortcut hint. */
export const ASK_KEY_HINT = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘J" : "Ctrl J";
